import { useEffect, useMemo, useRef, useState } from "react";
import { useLayer } from "@/lib/layer-stack";
import { I } from "@/components/Icon";
import { SyncGlyph } from "./SyncGlyph";

export type EntityItem = {
  id: string | number;
  name: string;
  sub?: string;
  tint: string;
  initials: string;
  /** When provided, rendered as an <img> inside the square tile
   *  instead of the initials. Falls back to initials on load error. */
  iconUrl?: string | null;
};

export function EntityTile({
  item,
  size = "md",
  overrideColor,
}: {
  item: { tint: string; initials: string; iconUrl?: string | null };
  size?: "sm" | "md";
  overrideColor?: string;
}) {
  const [failed, setFailed] = useState(false);
  const classes = size === "sm" ? "app-tile sm" : "app-tile";
  if (item.iconUrl && !failed) {
    return (
      <span
        className={classes}
        style={{
          background: "transparent",
          padding: 0,
          overflow: "hidden",
          flex: "none",
        }}
      >
        <img
          src={item.iconUrl}
          alt=""
          onError={() => setFailed(true)}
          style={{
            width: "100%",
            height: "100%",
            maxWidth: "100%",
            maxHeight: "100%",
            objectFit: "cover",
            display: "block",
          }}
        />
      </span>
    );
  }
  return (
    <span
      className={classes}
      style={{ background: item.tint, color: overrideColor || "#fff" }}
    >
      {item.initials}
    </span>
  );
}

export function MultiEntityPicker({
  items,
  values,
  onChange,
  label,
  width,
  pluralLabel,
  allLabel,
  syncNow,
}: {
  items: EntityItem[];
  values: (string | number)[];
  onChange: (next: (string | number)[]) => void;
  label?: string;
  width?: number;
  pluralLabel?: string;
  allLabel?: string;
  // Optional sticky footer with a "Synchronize Now" button. Used by
  // the app picker on Overview + Ads Manager to trigger ASA app
  // discovery on demand (the daily worker job covers the unattended
  // case; this is for the user who just added an app in App Store
  // Connect and doesn't want to wait until 04:00 UTC).
  syncNow?: {
    onClick: () => void;
    loading: boolean;
    label?: string;
    /** Optional message shown under the button — e.g. "Discovered 2
     *  new apps" after a successful sync. Caller controls the text
     *  + lifecycle so the picker stays presentation-only. */
    status?: string | null;
  };
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  useLayer(open, () => setOpen(false));

  const allSelected = values.length === items.length && items.length > 0;
  const someSelected = values.length > 0 && !allSelected;

  // Row click is single-select: clicking an item replaces the
  // current selection with just that item and closes the dropdown.
  // Multi-select (all items) is still available via the "Select
  // all" checkbox row at the top of the list. This matches how
  // most users switch between apps — they want a radio, not a
  // checkbox — and keeps the all-apps path one click away.
  const selectOnly = (id: string | number) => {
    onChange([id]);
    setOpen(false);
  };
  const toggleAll = () => {
    onChange(allSelected ? (items[0] ? [items[0].id] : []) : items.map((i) => i.id));
  };

  const trigger = useMemo<{
    tile: { tint: string; initials: string; iconUrl?: string | null };
    name: string;
    sub: string;
  }>(() => {
    if (allSelected) {
      return {
        tile: { tint: "linear-gradient(135deg,#6e62ff,#2fb46f)", initials: "\u2217" },
        name: allLabel || "All " + (pluralLabel || "items"),
        sub: `${items.length} ${pluralLabel || "items"} selected`,
      };
    }
    if (values.length === 1) {
      const only = items.find((i) => i.id === values[0]) || items[0];
      if (!only) {
        return {
          tile: { tint: "var(--c-text-4)", initials: "?" },
          name: "—",
          sub: "",
        };
      }
      return {
        tile: { tint: only.tint, initials: only.initials, iconUrl: only.iconUrl },
        name: only.name,
        sub: only.sub || "",
      };
    }
    return {
      tile: { tint: "var(--c-surface-sunk)", initials: String(values.length) },
      name: `${values.length} ${pluralLabel || "items"}`,
      sub:
        values
          .slice(0, 2)
          .map((id) => items.find((i) => i.id === id)?.name)
          .filter(Boolean)
          .join(", ") + (values.length > 2 ? ` +${values.length - 2}` : ""),
    };
  }, [allSelected, values, items, pluralLabel, allLabel]);

  const multiBadge = values.length > 1 && !allSelected;

  return (
    <div ref={ref} style={{ position: "relative", display: "inline-block", minWidth: width || 220 }}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="app-picker"
        data-open={open}
        style={{ width: "100%", minWidth: 0 }}
      >
        <EntityTile
          item={trigger.tile}
          overrideColor={multiBadge ? "var(--c-text-2)" : undefined}
        />
        <span className="app-meta" style={{ minWidth: 0 }}>
          {label && (
            <span
              style={{
                fontSize: 10,
                textTransform: "uppercase",
                letterSpacing: 0.5,
                color: "var(--c-text-4)",
                display: "block",
                lineHeight: 1.1,
              }}
            >
              {label}
            </span>
          )}
          <span className="app-name">{trigger.name}</span>
          {trigger.sub && <span className="app-sub">{trigger.sub}</span>}
        </span>
        <span className="app-chev">{I.chevronDown}</span>
      </button>
      {open && (
        <div className="app-popover" style={{ minWidth: width ? width + 40 : 300, padding: 6 }}>
          {label && <div className="app-popover-head" style={{ padding: "8px 10px 6px" }}>{label}</div>}
          {items.length > 1 && (
            <label
              className="multi-opt"
              onClick={(e) => {
                e.preventDefault();
                toggleAll();
              }}
            >
              <span className={"mx-check " + (allSelected ? "on" : someSelected ? "some" : "")}>
                {allSelected && (
                  <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                    <path
                      d="m3 8 3 3 7-7"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
                {someSelected && <span className="mx-dash" />}
              </span>
              <span className="app-meta">
                <span className="app-name">{allLabel || `All ${pluralLabel || "items"}`}</span>
                <span className="app-sub">
                  {allSelected
                    ? "Everything selected"
                    : someSelected
                    ? `${values.length} of ${items.length} selected`
                    : "Nothing selected"}
                </span>
              </span>
            </label>
          )}
          {items.length > 1 && (
            <div style={{ height: 1, background: "var(--c-divider)", margin: "4px 2px" }} />
          )}
          {items.map((a) => {
            // `checked` now reflects "this is the single current
            // selection" (or one of the set when All is on). We
            // keep the visual affordance so users can still see
            // which item is active without cross-checking the
            // trigger.
            const checked = values.includes(a.id);
            return (
              <label
                key={String(a.id)}
                className="multi-opt"
                onClick={(e) => {
                  e.preventDefault();
                  selectOnly(a.id);
                }}
              >
                <span className={"mx-check " + (checked ? "on" : "")}>
                  {checked && (
                    <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                      <path
                        d="m3 8 3 3 7-7"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </span>
                <EntityTile item={a} />
                <span className="app-meta">
                  <span className="app-name">{a.name}</span>
                  {a.sub && <span className="app-sub">{a.sub}</span>}
                </span>
              </label>
            );
          })}
          {syncNow && (
            <>
              <div
                style={{
                  height: 1,
                  background: "var(--c-divider)",
                  margin: "6px 2px 4px",
                }}
              />
              <button
                type="button"
                onClick={(e) => {
                  // Don't close the popover — the user may want to
                  // see the discovered count before navigating away,
                  // and a successful sync should feel like a
                  // confirmation inside the picker, not a dismissal.
                  e.preventDefault();
                  e.stopPropagation();
                  if (!syncNow.loading) syncNow.onClick();
                }}
                disabled={syncNow.loading}
                style={{
                  width: "100%",
                  padding: "8px 10px",
                  background: "transparent",
                  border: "none",
                  borderRadius: 6,
                  cursor: syncNow.loading ? "wait" : "pointer",
                  color: syncNow.loading
                    ? "var(--c-text-3)"
                    : "var(--c-accent-600)",
                  fontSize: 13,
                  fontWeight: 500,
                  textAlign: "left",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
                onMouseEnter={(e) => {
                  if (!syncNow.loading) {
                    e.currentTarget.style.background =
                      "var(--c-surface-sunk)";
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                }}
              >
                <SyncGlyph spinning={syncNow.loading} />
                <span>
                  {syncNow.loading
                    ? "Synchronizing…"
                    : syncNow.label ?? "Synchronize Now"}
                </span>
              </button>
              {syncNow.status && (
                <div
                  style={{
                    padding: "2px 10px 6px 32px",
                    fontSize: 11.5,
                    color: "var(--c-text-3)",
                  }}
                >
                  {syncNow.status}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
